'use client'

import type { Dispatch, SetStateAction } from 'react'
import { getSupabaseClient } from '@/lib/supabase-client'
import { getValidatedAccessToken } from '@/lib/supabase-validated-session'
import { resetUser } from '@/instrumentation-client'
import { forgetAuthSource, rememberAuthSource, type PendingAuthSource } from '@/lib/pending-signup'
import { createAppleIdTokenNonce, isNativeAppleAuthAvailable, type NativeAppleFullName, signInWithNativeApple } from '@/lib/native-apple-auth'
import { createGoogleIdTokenNonce, isNativeGoogleAuthAvailable, signInWithNativeGoogle } from '@/lib/native-google-auth'
import { classifyNativeAuthFailure, type NativeAuthFailure } from '@/lib/native-auth-failure'
import { normalizePreferredLanguage, PreferredLanguage } from '@/server/domain/language'
import type { AccountSnapshot } from '@/lib/account-snapshot'
import type { AuthChallengeMethod } from '@/server/domain/clients'

/** La cuenta en contexto: el snapshot que manda el servidor más su id. */
export interface User extends AccountSnapshot {
  id: string
}

export type AuthLanguage = PreferredLanguage
export type AuthOperationResult = {
  success: boolean
  isNewUser?: boolean
  error?: string
  /**
   * Presente solo cuando falla un plugin nativo de login social. Lleva el outcome
   * (qué hacer) y el código del plugin (qué pasó, para telemetría). Excluyente con
   * `error`: si viene esto, la copy sale de i18n en la pantalla.
   */
  nativeFailure?: NativeAuthFailure
  verificationMethod?: AuthChallengeMethod
}
export type AuthCompletionSource = PendingAuthSource | 'session'

export interface AuthStateHandlers {
  // El setter de `useState`: admite la forma funcional, para que una escritura
  // tras un `await` actualice el estado ACTUAL y no una copia vieja.
  setUser: Dispatch<SetStateAction<User | null>>
  // El idioma sin sesión; con sesión manda el de la cuenta.
  setGuestLanguage: (language: AuthLanguage) => void
  setIsLoading: (isLoading: boolean) => void
}

type SignedOutStateHandlers = Pick<AuthStateHandlers, 'setUser' | 'setGuestLanguage'>

export type FetchUserDataInput = {
  accessToken: string
  /**
   * Locale crudo del intento (OTP y login social). El sync (`attemptAccountSync`)
   * es el ÚNICO resolutor del idioma → `preferredLanguage`; el caller no
   * pre-resuelve nada.
   */
  locale?: string
  source: PendingAuthSource
}

interface LocaleInput {
  locale?: string
}

interface ProviderSignInInput extends LocaleInput {
  fetchUserData: (input: FetchUserDataInput) => Promise<AuthOperationResult>
}

type NativeSocialProvider = Extract<AuthCompletionSource, 'apple' | 'google'>

interface NativeSignInResult {
  idToken: string
  fullName?: NativeAppleFullName
}

interface NativeProviderConfig {
  source: NativeSocialProvider
  logLabel: string
  isAvailable: () => boolean
  createNonce: () => Promise<{ nativeRequestNonce: string; supabaseNonce: string }>
  signIn: (input: { nonce: string }) => Promise<NativeSignInResult>
  afterSignIn?: (result: NativeSignInResult) => Promise<void>
}

const supabase = getSupabaseClient()

const LANGUAGE_CACHE_KEY = 'papanoelchat_user_language'
const LANGUAGE_CACHE_TIMESTAMP_KEY = 'papanoelchat_language_timestamp'
const LANGUAGE_CACHE_TTL = 24 * 60 * 60 * 1000

export function getCachedLanguage(): AuthLanguage | null {
  if (typeof window === 'undefined') return null

  try {
    const cached = localStorage.getItem(LANGUAGE_CACHE_KEY)
    const timestamp = localStorage.getItem(LANGUAGE_CACHE_TIMESTAMP_KEY)

    if (!cached || !timestamp) return null

    const age = Date.now() - parseInt(timestamp, 10)
    if (age > LANGUAGE_CACHE_TTL) {
      clearLanguageCache()
      return null
    }

    return normalizePreferredLanguage(cached)
  } catch {
    return null
  }
}

export function cacheLanguage(language: AuthLanguage): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(LANGUAGE_CACHE_KEY, language)
    localStorage.setItem(LANGUAGE_CACHE_TIMESTAMP_KEY, Date.now().toString())
    document.documentElement.lang = language
  } catch {
    // Ignore localStorage errors.
  }
}

export function clearLanguageCache(): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(LANGUAGE_CACHE_KEY)
    localStorage.removeItem(LANGUAGE_CACHE_TIMESTAMP_KEY)
  } catch {
    // Ignore localStorage errors.
  }
}

function storeAuthLanguage({ locale }: LocaleInput): void {
  if (locale && typeof document !== 'undefined') {
    document.cookie = `auth-language=${locale}; path=/; max-age=${30 * 24 * 60 * 60}`
  }
}

function getSocialRedirectTo(): string | undefined {
  return typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined
}

async function startSocialOAuth({
  locale,
  provider,
  errorMessage,
  logLabel
}: LocaleInput & {
  provider: NativeSocialProvider
  errorMessage: string
  logLabel: string
}): Promise<AuthOperationResult> {
  try {
    storeAuthLanguage({ locale })
    rememberAuthSource(provider)

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: getSocialRedirectTo()
      }
    })

    if (error) {
      forgetAuthSource()
      console.error(`${logLabel} sign-in error:`, error)
      return { success: false, error: errorMessage }
    }

    return { success: true }
  } catch (error) {
    forgetAuthSource()
    console.error(`${logLabel} sign-in error:`, error)
    return { success: false, error: 'Error de conexión' }
  }
}

export function startGoogleOAuth({ locale }: LocaleInput): Promise<AuthOperationResult> {
  return startSocialOAuth({
    locale,
    provider: 'google',
    errorMessage: 'No hemos podido iniciar sesión con Google',
    logLabel: 'Google OAuth'
  })
}

/**
 * Traduce el rechazo del plugin nativo a un resultado que la pantalla pueda usar.
 * Devuelve `nativeFailure` (outcome + código) y NUNCA `error`: la copy la elige
 * `auth-screen` desde i18n, o EN y PT heredarían el castellano.
 */
function failNativeProviderSignIn(config: NativeProviderConfig, error?: unknown): AuthOperationResult {
  forgetAuthSource()

  const nativeFailure = classifyNativeAuthFailure(error)

  if (error && nativeFailure.outcome !== 'cancelled') {
    console.error(`${config.logLabel} sign-in error:`, error)
  }

  return { success: false, nativeFailure }
}

function getNativeProviderAccessToken(
  data: { session?: { access_token: string } | null },
  config: NativeProviderConfig
): string | AuthOperationResult {
  if (!data.session) {
    return failNativeProviderSignIn(config)
  }

  return data.session.access_token
}

async function syncNativeProviderAccount({
  accessToken,
  locale,
  fetchUserData,
  source
}: ProviderSignInInput & {
  accessToken: string
  source: NativeSocialProvider
}): Promise<AuthOperationResult> {
  const syncResult = await fetchUserData({
    accessToken,
    locale,
    source
  })

  if (!syncResult.success) {
    return { success: false, error: syncResult.error || 'Error de conexión' }
  }

  return { success: true, isNewUser: syncResult.isNewUser || false }
}

async function startNativeProviderSignIn(
  input: ProviderSignInInput,
  config: NativeProviderConfig
): Promise<AuthOperationResult> {
  if (!config.isAvailable()) {
    return { success: false, nativeFailure: { outcome: 'no_account' } }
  }

  try {
    storeAuthLanguage({ locale: input.locale })
    rememberAuthSource(config.source)

    const nonce = await config.createNonce()
    const nativeResult = await config.signIn({ nonce: nonce.nativeRequestNonce })
    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: config.source,
      token: nativeResult.idToken,
      nonce: nonce.supabaseNonce
    })

    if (error) {
      return failNativeProviderSignIn(config, error)
    }

    const accessToken = getNativeProviderAccessToken(data, config)
    if (typeof accessToken !== 'string') {
      return accessToken
    }

    await config.afterSignIn?.(nativeResult)

    return syncNativeProviderAccount({
      ...input,
      accessToken,
      source: config.source
    })
  } catch (error) {
    return failNativeProviderSignIn(config, error)
  }
}


const NATIVE_PROVIDER_CONFIGS: Record<NativeSocialProvider, NativeProviderConfig> = {
  google: {
    source: 'google',
    logLabel: 'Native Google',
    isAvailable: isNativeGoogleAuthAvailable,
    createNonce: createGoogleIdTokenNonce,
    signIn: signInWithNativeGoogle
  },
  apple: {
    source: 'apple',
    logLabel: 'Native Apple',
    isAvailable: isNativeAppleAuthAvailable,
    createNonce: createAppleIdTokenNonce,
    signIn: signInWithNativeApple,
    afterSignIn: ({ fullName }) => updateAppleFullName(fullName)
  }
}

export async function startGoogleSignIn(input: ProviderSignInInput): Promise<AuthOperationResult> {
  if (isNativeGoogleAuthAvailable()) {
    return startNativeProviderSignIn(input, NATIVE_PROVIDER_CONFIGS.google)
  }

  return startGoogleOAuth({ locale: input.locale })
}

function getAppleFullNameData(fullName: NativeAppleFullName): Record<string, string> | null {
  const nameParts = [fullName.givenName, fullName.familyName].filter(Boolean)
  if (nameParts.length === 0) return null

  return {
    full_name: nameParts.join(' '),
    ...(fullName.givenName ? { given_name: fullName.givenName } : {}),
    ...(fullName.familyName ? { family_name: fullName.familyName } : {})
  }
}

async function updateAppleFullName(fullName?: NativeAppleFullName): Promise<void> {
  if (!fullName) return

  const data = getAppleFullNameData(fullName)
  if (!data) return

  try {
    await supabase.auth.updateUser({ data })
  } catch (error) {
    console.error('Apple full name update failed:', error)
  }
}

export async function startAppleSignIn(input: ProviderSignInInput): Promise<AuthOperationResult> {
  if (isNativeAppleAuthAvailable()) {
    return startNativeProviderSignIn(input, NATIVE_PROVIDER_CONFIGS.apple)
  }

  return startSocialOAuth({
    locale: input.locale,
    provider: 'apple',
    errorMessage: 'No hemos podido iniciar sesión con Apple',
    logLabel: 'Apple OAuth'
  })
}

/** El único cierre de sesión en cliente: logout, borrado de cuenta y un 401 en el sync. */
export function resetClientAuthState({
  setUser,
  setGuestLanguage
}: SignedOutStateHandlers): void {
  resetUser()
  setUser(null)
  clearLanguageCache()
  setGuestLanguage('es')
}

export function requireAuthenticatedUser(user: User | null, action: string): User {
  if (!user) {
    throw new Error(`User must be authenticated to ${action}`)
  }
  return user
}

export async function sendOtpEmail(email: string, locale?: string): Promise<AuthOperationResult> {
  try {
    const response = await fetch('/api/auth/send-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() })
    })
    const data = await response.json()

    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Error al enviar código' }
    }

    storeAuthLanguage({ locale })
    return {
      success: true,
      verificationMethod: data.verificationMethod === 'reusable_code'
        ? 'reusable_code'
        : 'email_otp'
    }
  } catch (error) {
    console.error('Send OTP error:', error)
    return { success: false, error: 'Error de conexión' }
  }
}

function failPendingOtp(error: string): AuthOperationResult {
  forgetAuthSource()
  return { success: false, error }
}

export async function verifyOtpCode({
  email,
  code,
  locale,
  verificationMethod = 'email_otp',
  fetchUserData
}: {
  email: string
  code: string
  locale?: string
  verificationMethod?: AuthChallengeMethod
  fetchUserData: (input: FetchUserDataInput) => Promise<AuthOperationResult>
}): Promise<AuthOperationResult> {
  try {
    rememberAuthSource('otp')

    const normalizedEmail = email.trim().toLowerCase()
    const { data, error } = verificationMethod === 'reusable_code'
      ? await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: code
      })
      : await supabase.auth.verifyOtp({
        email: normalizedEmail,
        token: code,
        type: 'email'
      })

    if (error) {
      console.error('Verify OTP error:', error)
      return failPendingOtp('Código inválido o expirado')
    }

    if (!data.user || !data.session) {
      return failPendingOtp('Usuario no encontrado')
    }

    const syncResult = await fetchUserData({
      accessToken: data.session.access_token,
      locale,
      source: 'otp'
    })

    if (!syncResult.success) {
      return { success: false, error: syncResult.error || 'Error de conexión' }
    }

    return { success: true, isNewUser: syncResult.isNewUser || false }
  } catch (error) {
    console.error('Verify OTP error:', error)
    return failPendingOtp('Error de conexión')
  }
}

export async function logoutCurrentSession(handlers: SignedOutStateHandlers): Promise<void> {
  try {
    const accessToken = await getValidatedAccessToken()

    if (accessToken) {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }).catch((error) => {
        console.error('Error calling logout API:', error)
      })
    }

    resetClientAuthState(handlers)
    await supabase.auth.signOut()
  } catch (error) {
    console.error('Logout error:', error)
    resetClientAuthState(handlers)
    await supabase.auth.signOut()
  }
}

export async function deleteCurrentAccount(handlers: SignedOutStateHandlers): Promise<{ success: boolean; error?: string }> {
  try {
    const accessToken = await getValidatedAccessToken()
    if (!accessToken) {
      return { success: false, error: 'No hay sesión activa' }
    }

    const response = await fetch('/api/account', {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    })

    if (!response.ok) {
      const data = await response.json()
      return {
        success: false,
        error: data.error?.message || 'Error al eliminar la cuenta'
      }
    }

    resetClientAuthState(handlers)
    await supabase.auth.signOut()
    return { success: true }
  } catch (error) {
    console.error('Delete account error:', error)
    return { success: false, error: 'Error de conexión' }
  }
}
